import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import Header from "../components/Header";
import { Compass, BookOpen, Target, Coins, TrendingUp, Shield, ArrowUpRight, Sparkles, Quote } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/10653941/pexels-photo-10653941.jpeg";

const PILLARS = [
  { slug: "discover", name: "Discover a Vocation", icon: Compass,    body: "Find the role that fits your mind, strengths and non-negotiables.", accent: "text-sky-400", border: "hover:border-sky-400/50" },
  { slug: "learn",    name: "Learn the Vocation", icon: BookOpen,    body: "A grounded library of executive practice. 271 volumes, searchable, cited.", accent: "text-primary",   border: "hover:border-primary/50" },
  { slug: "practice", name: "Practice the Vocation", icon: Target,   body: "Operating cadence, 30-60-90 plans, decision rights — ready to run.", accent: "text-emerald-400", border: "hover:border-emerald-400/50" },
  { slug: "earn",     name: "Earn a Living",       icon: Coins,      body: "Compensation benchmarks, equity ranges, offer analysis.", accent: "text-amber-400", border: "hover:border-amber-400/50" },
  { slug: "advance",  name: "Advance a Vocation",  icon: TrendingUp, body: "Stage-by-stage career ladders, succession readiness, calibrated growth.", accent: "text-fuchsia-400", border: "hover:border-fuchsia-400/50" },
  { slug: "protect",  name: "Protect the Dignity of Every Worker", icon: Shield, body: "Rights, safety, ethics — grounded in global employment principles.", accent: "text-rose-400", border: "hover:border-rose-400/50" },
];

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-[0.08] dark:opacity-[0.15]" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/70 to-background" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-20 md:pb-28">
          <div className="label-caps mb-6" data-testid="hero-eyebrow">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary mr-2 align-middle" />
            Mahakali Tribunal · Intelligence · Justice · Wealth
          </div>
          <h1 data-testid="hero-title" className="text-4xl sm:text-6xl lg:text-7xl tracking-tighter font-semibold leading-[1.02] max-w-5xl">
            The global system for <span className="text-primary">human vocation.</span>
          </h1>
          <p data-testid="hero-sub" className="mt-8 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
            Executives by Mahakali Tribunal is a disciplined, evidence-grounded platform for everyone who takes
            their work seriously — founders, C-suite, researchers, specialists and the workers who hold the line.
            Discover a vocation. Learn it. Practice it. Earn from it. Advance it. Protect it.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Button size="lg" data-testid="hero-cta-primary" onClick={() => nav("/auth?mode=signup")} className="rounded-full px-8 h-12">
              Enter the tribunal <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
            <Button size="lg" variant="outline" data-testid="hero-cta-secondary" onClick={() => nav("/auth")} className="rounded-full px-8 h-12">
              Sign in
            </Button>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            {[
              { k: "Volumes", v: "271" },
              { k: "Chapters", v: "5,000+" },
              { k: "Vocations covered", v: "60+" },
              { k: "Pillars", v: "6" },
            ].map((s) => (
              <div key={s.k} className="border-l border-border pl-4" data-testid={`stat-${s.k.toLowerCase()}`}>
                <div className="font-mono text-2xl md:text-3xl tabular-nums">{s.v}</div>
                <div className="label-caps mt-1">{s.k}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-28">
        <div className="mb-14 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <div className="label-caps mb-3">Six pillars, one spine</div>
            <h2 className="text-3xl sm:text-5xl tracking-tight font-medium max-w-2xl">
              A lifetime of work, structured.
            </h2>
          </div>
          <p className="text-muted-foreground max-w-md">
            Every pillar is grounded in the same body of expert knowledge — 2,000+ pages of operating principles,
            cadences, decision frameworks, metrics and worker protections.
          </p>
        </div>

        <div className="grid md:grid-cols-6 gap-4">
          {PILLARS.map((p, i) => (
            <Link key={p.slug} to="/auth?mode=signup" data-testid={`pillar-${p.slug}`}
              className={`${i === 0 || i === 5 ? "md:col-span-3" : i === 1 || i === 2 ? "md:col-span-2" : "md:col-span-2"} p-6 md:p-8 rounded-2xl border border-border bg-card ${p.border} transition-colors duration-200 min-h-[200px] flex flex-col justify-between`}>
              <div>
                <p.icon className={`h-5 w-5 mb-4 ${p.accent}`} />
                <h3 className="text-xl md:text-2xl font-medium tracking-tight">{p.name}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
              <div className="mt-6 label-caps flex items-center gap-1">
                Enter <ArrowUpRight className="h-3 w-3" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TRIBUNAL ADVISOR */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24 border-t border-border">
        <div className="grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <div className="label-caps mb-3">The Tribunal Advisor</div>
            <h2 className="text-3xl sm:text-5xl tracking-tight font-medium">Ask. Get grounded answers.</h2>
            <p className="mt-6 text-muted-foreground text-lg leading-relaxed max-w-xl">
              A disciplined AI counsel that answers only from a vetted knowledge base — with inline citations.
              No hallucinated statutes. No invented frameworks. Just the sources, interpreted.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {[
                "What should a CFO own in the first 90 days?",
                "How do reserved matters differ from delegations?",
                "What is a worker's remedy for retaliation?",
                "How do I run a decision log in a family-owned firm?",
              ].map((q) => (
                <div key={q} data-testid={`sample-q-${q.length}`} className="px-3 py-1.5 rounded-full border border-border text-xs text-muted-foreground">
                  "{q}"
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2">
            <Card className="p-6 bg-card/80 border-border" data-testid="tribunal-preview">
              <div className="label-caps mb-3 flex items-center gap-1.5">
                <Sparkles className="h-3 w-3 text-primary" /> Grounded response
              </div>
              <div className="text-sm leading-relaxed text-foreground/90">
                A chief executive's reserved matters typically include strategy approval, CEO succession,
                material capital allocation and risk appetite <span className="text-primary font-mono text-[11px]">[1]</span>.
                Delegation thresholds should be documented and periodically re-tested
                <span className="text-primary font-mono text-[11px]">[2]</span>.
              </div>
              <div className="mt-5 pt-5 border-t border-border space-y-1.5">
                {[
                  "1 · Ownership Governance and Management — Practical Implementation",
                  "2 · Executive Decision Rights — Framework and Operating Model",
                ].map((c) => (
                  <div key={c} className="text-[11px] font-mono text-muted-foreground">{c}</div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* DIGNITY SPINE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <div className="rounded-3xl border border-border p-10 md:p-16 grain relative overflow-hidden bg-card">
          <Shield className="h-7 w-7 text-rose-400 mb-6" />
          <h2 className="text-3xl sm:text-5xl tracking-tighter font-semibold max-w-3xl">
            Every pillar rests on one principle:<br />
            <span className="text-rose-400">Protect the dignity of every worker.</span>
          </h2>
          <p className="mt-6 text-muted-foreground text-lg max-w-2xl">
            From boardroom to shop floor, from a founder's first hire to a retiring veteran — the tribunal holds
            the same standard. Fair process. Honest reward. Safe conduct. Protected voice.
          </p>
          <div className="mt-10">
            <Button size="lg" onClick={() => nav("/auth?mode=signup")} data-testid="cta-dignity" className="rounded-full px-8 h-12">
              Create your profile
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} Mahakali Tribunal · Executives</div>
          <div className="font-mono">executives.mahakalitribunal.com</div>
        </div>
      </footer>
    </div>
  );
}
